/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: footer.section.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaLink, FaLocationDot } from 'react-icons/fa6';

import Logo from '@/shared/logo';
import Section from '@/shared/section';
import App from '@/constants/app';
import Statics from '@/constants/statics';
import { FaEnvelope, FaPhoneAlt } from 'react-icons/fa';
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const BottomNavigation: FunctionComponent = () => {
  const { t } = useTranslation('common');

  // const getPhone = (country: string) => {
  //   const index = Statics.PhoneItems.findIndex((item) => item.country === country);
  //   return Statics.PhoneItems[index];
  // };

  return (
    <Section id="footer" className="bg-black">
      <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        <div className="col-span-1 flex flex-col items-center justify-start gap-5">
          <Logo logoSize={80} titleSize={40} tagLineSize={21} />
          <motion.p
            className="text-md text-white text-justify font-inter font-light"
            initial="initial"
            whileInView="entry"
            viewport={{ once: true }}
            variants={{
              initial: {
                x: 100,
                opacity: 0
              },
              entry: {
                x: 0,
                opacity: 1
              }
            }}
            transition={{
              duration: 0.5
            }}
          >
            {t('footer.desc')}
          </motion.p>
          {/* <div className="w-full flex items-center justify-start gap-3">
            <motion.p
              className="text-lg text-theme-900 font-montserrat font-medium"
              initial="initial"
              whileInView="entry"
              viewport={{ once: true }}
              variants={{
                initial: {
                  x: -100,
                  opacity: 0
                },
                entry: {
                  x: 0,
                  opacity: 1
                }
              }}
              transition={{
                duration: 0.5
              }}
            >
              {t('footer.connect-us')} -{' '}
            </motion.p>
            <div className="flex items-center justify-center gap-3">
              {Statics.SocialItems.map(({ id, icon: Icon, href }, idx: number) => (
                <motion.a
                  key={id}
                  className="w-[30px] h-[30px] bg-theme-900/80 hover:bg-theme-900 text-black flex flex-col items-center justify-center rounded-full transition-all duration-350"
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  initial="initial"
                  whileInView="entry"
                  viewport={{ once: true }}
                  variants={{
                    initial: {
                      y: 50 + idx * 25,
                      opacity: 0
                    },
                    entry: {
                      y: 0,
                      opacity: 1
                    }
                  }}
                  transition={{
                    duration: 0.5
                  }}
                >
                  <Icon className="text" />
                </motion.a>
              ))}
            </div>
          </div> */}
        </div>
        <div className="col-span-1 flex flex-col items-center justify-start gap-5">
          <div className="w-full min-h-[40px] border-b-[1px] border-b-white flex flex-col items-center justify-center">
            <motion.p
              className="w-full text-lg text-theme-900 font-montserrat font-medium"
              initial="initial"
              whileInView="entry"
              viewport={{ once: true }}
              variants={{
                initial: {
                  x: -100,
                  opacity: 0
                },
                entry: {
                  x: 0,
                  opacity: 1
                }
              }}
              transition={{
                duration: 0.5
              }}
            >
              {t('footer.quick-links.title')}
            </motion.p>
          </div>
          <ul className="w-full flex flex-col items-start justify-start gap-2">
            {Statics.FooterLinks.map(({ id, label, href }, idx: number) => (
              <motion.li
                key={id}
                className="py-1"
                initial="initial"
                whileInView="entry"
                viewport={{ once: true }}
                variants={{
                  initial: {
                    x: 50 + idx * 10,
                    opacity: 0
                  },
                  entry: {
                    x: 0,
                    opacity: 1
                  }
                }}
                transition={{
                  duration: 0.5
                }}
              >
                <a
                  className="text-md text-white/60 hover:text-white font-inter flex items-center justify-start gap-2 transition-all duration-350"
                  href={href}
                >
                  <FaLink />
                  {t(label)}
                </a>
              </motion.li>
            ))}
          </ul>
        </div>
        <div className="col-span-1 flex flex-col items-center justify-start gap-5">
          <div className="w-full min-h-[40px] border-b-[1px] border-b-white flex flex-col items-center justify-center">
            <motion.p
              className="w-full text-lg text-theme-900 font-montserrat font-medium"
              initial="initial"
              whileInView="entry"
              viewport={{ once: true }}
              variants={{
                initial: {
                  x: -100,
                  opacity: 0
                },
                entry: {
                  x: 0,
                  opacity: 1
                }
              }}
              transition={{
                duration: 0.5
              }}
            >
              {t('footer.quick-contact.title')}
            </motion.p>
          </div>
          <ul className="w-full flex flex-col items-start justify-start gap-5">
            {Statics.AddressItems.map(({ id, country, address }, idx: number) => (
              <motion.li
                key={id}
                className="flex flex-col items-center justify-start gap-2"
                initial="initial"
                whileInView="entry"
                viewport={{ once: true }}
                variants={{
                  initial: {
                    x: 50 + idx * 20,
                    opacity: 0
                  },
                  entry: {
                    x: 0,
                    opacity: 1
                  }
                }}
                transition={{
                  duration: 0.5
                }}
              >
                <h5 className="w-full text-white font-medium">{country}</h5>
                <a
                  className="w-full text-md text-white/60 hover:text-white flex items-center justify-start gap-[10px] transition-all duration-350"
                  href={`http://maps.google.com/?q=${address}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="w-[20px] h-[20px]">
                    <FaLocationDot />
                  </div>
                  <p className="w-[calc(100%-30px)]font-inter">{address}</p>
                </a>
                <a
                  className="w-full text-md text-white/60 hover:text-white flex items-center justify-start gap-[10px] transition-all duration-350"
                  href={Statics.EmailItems[0].href}
                >
                  <div className="w-[20px] h-[20px]">
                    <FaEnvelope />
                  </div>
                  <p className="w-[calc(100%-30px)]font-inter">{Statics.EmailItems[0].label}</p>
                </a>
                <a
                  className="w-full text-md text-white/60 hover:text-white flex items-center justify-start gap-[10px] transition-all duration-350"
                  href="tel:+19724489127"
                >
                  <div className="w-[20px] h-[20px]">
                    <FaPhoneAlt />
                  </div>
                  <p className="w-[calc(100%-30px)] font-inter">+19724489127</p>
                </a>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
};

const Copyright: FunctionComponent = () => {
  return (
    <motion.div
      className="w-full bg-theme-900 min-h-[40px] flex items-center justify-center px-5"
      initial="initial"
      whileInView="entry"
      viewport={{ once: true }}
      variants={{
        initial: {
          y: -50,
          opacity: 0
        },
        entry: {
          y: 0,
          opacity: 1
        }
      }}
      transition={{
        duration: 0.5
      }}
    >
      <p className="text-xs sm:text-sm font-inter font-light">
        &copy; {new Date().getFullYear()}{' '}
        <span className="text-black font-montserrat font-bold">{App.Name}</span>. All
        Rights Reserved
      </p>
    </motion.div>
  );
};

const Footer = {
  BottomNavigation,
  Copyright
};

export default Footer;
